import { useMemo, useState } from 'react';

import type {
  ApplyArAdvance,
  ArAdvance,
  ArInvoiceListRow,
  ChartOfAccount,
  RefundArAdvance,
} from '@dcc-erp/shared';
import { dateToInputValue, fmt, fmtDate, toNumber } from '@dcc-erp/shared';
import { Icon } from '@dcc-erp/ui';

import { SearchSelect } from '../SearchSelect';
import {
  modalBody,
  modalBox,
  modalError,
  modalFooter,
  modalHeader,
  modalLabel,
  modalOverlay,
  modalTitle,
} from './modal-style';

// =============================================================================
// Router-free AR customer-advance (เงินรับล่วงหน้า / deposits, AR-12) list. The
// route owns data fetching (useArAdvances) + the lifecycle mutations + the create
// modal; this component owns presentation only — a flat table carrying the
// advance header (ADV no / customer / advanceDate / amount / balance) + the OPEN /
// CLOSED status badge. Row actions: ดู / ตัดกับใบแจ้งหนี้ (apply, balance > 0) /
// คืนเงิน (refund, balance > 0). An advance posts a real receive JV on create; its
// remaining balance is reduced by apply (settles AR) or refund (returns cash).
// Plain prototype.css classes only.
// =============================================================================

// Lifecycle-status badge — same vocabulary as the other AR status cells.
function StatusBadge({ status }: { status: ArAdvance['status'] }) {
  if (status === 'CLOSED') {
    return (
      <span className="badge positive">
        <span className="bdot" />
        ปิดแล้ว
      </span>
    );
  }
  return (
    <span className="badge info">
      <span className="bdot" />
      เปิดอยู่
    </span>
  );
}

export interface AdvanceListProps {
  advances: ArAdvance[];
  onView: (advance: ArAdvance) => void;
  /** Apply part of the balance to an invoice — shown only when balance > 0. */
  onApply: (advance: ArAdvance) => void;
  /** Refund the remaining balance — shown only when balance > 0. */
  onRefund: (advance: ArAdvance) => void;
}

export function AdvanceList({ advances, onView, onApply, onRefund }: AdvanceListProps) {
  return (
    <div className="tbl-shell">
      <div className="tbl-wrap" style={{ maxHeight: 600 }}>
        <table className="tbl">
          <thead>
            <tr>
              <th>เลขที่</th>
              <th>ลูกค้า</th>
              <th>วันที่รับ</th>
              <th className="num">จำนวนเงิน</th>
              <th className="num">คงเหลือ</th>
              <th>สถานะ</th>
              <th style={{ width: 1 }} />
            </tr>
          </thead>
          <tbody>
            {advances.length === 0 ? (
              <tr>
                <td colSpan={7} className="muted" style={{ textAlign: 'center', padding: 20 }}>
                  ไม่มีข้อมูล
                </td>
              </tr>
            ) : (
              advances.map((adv) => {
                const balance = toNumber(adv.balance);
                const open = balance > 0;
                return (
                  <tr key={adv.id}>
                    <td>
                      <span className="code">{adv.voucherNo}</span>
                    </td>
                    <td>{adv.customerName ?? adv.customerId}</td>
                    <td>{fmtDate(adv.advanceDate)}</td>
                    <td className="num tnum">{fmt(toNumber(adv.amount), 2)}</td>
                    <td className="num tnum">{fmt(balance, 2)}</td>
                    <td>
                      <StatusBadge status={adv.status} />
                    </td>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      <div style={{ display: 'inline-flex', gap: 6 }}>
                        <button
                          className="btn ghost"
                          style={{ padding: '2px 8px', fontSize: 12 }}
                          onClick={() => onView(adv)}
                          title="ดูรายละเอียด"
                        >
                          <Icon name="eye" size={12} /> ดู
                        </button>
                        {open && (
                          <button
                            className="btn primary"
                            style={{ padding: '2px 8px', fontSize: 12 }}
                            onClick={() => onApply(adv)}
                            title="ตัดเงินรับล่วงหน้ากับใบแจ้งหนี้ (Dr รับล่วงหน้า / Cr ลูกหนี้)"
                          >
                            <Icon name="check" size={12} /> ตัดกับใบแจ้งหนี้
                          </button>
                        )}
                        {open && (
                          <button
                            className="btn ghost"
                            style={{ padding: '2px 8px', fontSize: 12 }}
                            onClick={() => onRefund(adv)}
                            title="คืนเงินรับล่วงหน้าส่วนที่เหลือ (Dr รับล่วงหน้า / Cr เงินสด)"
                          >
                            <Icon name="ar" size={12} /> คืนเงิน
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// =============================================================================
// Apply modal — pick one of the customer's outstanding invoices + an amount,
// capped to min(advance balance, invoice outstanding). The container fetches the
// advance detail (for the LIVE balance) + the customer's outstanding invoices;
// this presentational form owns the invoice select + amount + the cap guard so
// the submit payload ({ invoiceId, amount }) is unit-testable without a query
// client. Submit is blocked while amount > the cap (the backend re-checks in-tx).
// =============================================================================

type OpenInvoice = Pick<
  ArInvoiceListRow,
  'id' | 'voucherNo' | 'totalAmount' | 'paidAmount' | 'creditedAmount'
>;

export interface ApplyAdvanceFormProps {
  /** The live remaining balance of the advance being applied. */
  balance: number;
  openInvoices: OpenInvoice[];
  onSubmit: (data: ApplyArAdvance) => void;
  submitting: boolean;
  /** Server error from the apply mutation — kept the modal open + shown inline. */
  error?: { message: string } | null | undefined;
}

export function ApplyAdvanceForm({
  balance,
  openInvoices,
  onSubmit,
  submitting,
  error,
}: ApplyAdvanceFormProps) {
  const [invoiceId, setInvoiceId] = useState<string>('');
  const [amount, setAmount] = useState<number>(0);
  // Once the user has hand-edited the amount, stop auto-defaulting it from the
  // chosen invoice (so they can apply a partial amount).
  const [amountTouched, setAmountTouched] = useState(false);

  const outstandingById = useMemo(() => {
    const m: Record<string, number> = {};
    for (const inv of openInvoices) {
      // outstanding = total − paid − credited (Phase 2b).
      m[inv.id] =
        toNumber(inv.totalAmount) - toNumber(inv.paidAmount) - toNumber(inv.creditedAmount);
    }
    return m;
  }, [openInvoices]);

  const invoiceOptions = openInvoices.map((inv) => ({
    value: inv.id,
    label: `${inv.voucherNo} · คงค้าง ${fmt(outstandingById[inv.id] ?? 0, 2)}`,
  }));
  const invoiceLabel = (id: string) => {
    const hit = openInvoices.find((inv) => inv.id === id);
    return hit ? `${hit.voucherNo} · คงค้าง ${fmt(outstandingById[id] ?? 0, 2)}` : id;
  };

  const invoiceOutstanding = invoiceId ? (outstandingById[invoiceId] ?? 0) : 0;
  // The amount is capped to the smaller of the advance balance and the chosen
  // invoice's outstanding — applying more would over-draw the advance or over-pay
  // the invoice (the backend rejects both in-tx).
  const cap = invoiceId ? Math.min(balance, invoiceOutstanding) : balance;

  function chooseInvoice(id: string) {
    setInvoiceId(id);
    if (amountTouched) return;
    // Default the amount to the cap for the newly chosen invoice.
    const out = id ? (outstandingById[id] ?? 0) : 0;
    setAmount(Math.min(balance, out));
  }

  const overCap = amount > cap;
  const disabled = !invoiceId || amount <= 0 || overCap || submitting;

  function handleSubmit() {
    onSubmit({ invoiceId, amount });
  }

  return (
    <div style={modalBody}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '10px 12px',
          borderRadius: 8,
          background: 'var(--brand-tint)',
          fontSize: 14,
        }}
      >
        <span className="muted">ยอดคงเหลือของเงินรับล่วงหน้า</span>
        <b className="tnum" style={{ fontSize: 16 }}>
          {fmt(balance, 2)} บาท
        </b>
      </div>

      <div>
        <label style={modalLabel}>ใบแจ้งหนี้คงค้าง</label>
        <SearchSelect
          value={invoiceId}
          onChange={chooseInvoice}
          options={invoiceOptions}
          getLabel={invoiceLabel}
          placeholder="— เลือกใบแจ้งหนี้ —"
        />
      </div>

      <div>
        <label style={modalLabel}>จำนวนที่ตัด (สูงสุด {fmt(cap, 2)})</label>
        <input
          type="number"
          className="input tnum"
          aria-label="apply-amount"
          value={amount || ''}
          min={0}
          max={cap}
          step={0.01}
          onChange={(e) => {
            setAmount(Number(e.target.value));
            setAmountTouched(true);
          }}
          style={{
            width: '100%',
            height: 38,
            fontSize: 14,
            textAlign: 'right',
            ...(overCap ? { borderColor: 'var(--negative)', color: 'var(--negative)' } : {}),
          }}
        />
      </div>

      {overCap && (
        <div style={modalError}>
          จำนวนที่ตัดเกินยอดที่ตัดได้ (สูงสุด {fmt(cap, 2)}) — กรุณาปรับยอด
        </div>
      )}

      {error && <div style={modalError}>บันทึกไม่สำเร็จ: {error.message}</div>}

      <div style={modalFooter}>
        <button type="button" className="btn primary" disabled={disabled} onClick={handleSubmit}>
          <Icon name="check" size={14} /> {submitting ? 'กำลังบันทึก…' : 'ยืนยันการตัด'}
        </button>
      </div>
    </div>
  );
}

export interface ApplyAdvanceModalProps {
  advance: ArAdvance;
  /** The live balance (from the detail query); falls back to the row balance. */
  balance: number;
  openInvoices: OpenInvoice[];
  loadingInvoices?: boolean;
  onSubmit: (data: ApplyArAdvance) => void;
  onClose: () => void;
  submitting: boolean;
  error?: { message: string } | null;
}

export function ApplyAdvanceModal({
  advance,
  balance,
  openInvoices,
  loadingInvoices = false,
  onSubmit,
  onClose,
  submitting,
  error,
}: ApplyAdvanceModalProps) {
  return (
    <div style={modalOverlay} onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} style={{ ...modalBox, width: 560 }}>
        <div style={modalHeader}>
          <h3 style={modalTitle}>ตัดเงินรับล่วงหน้ากับใบแจ้งหนี้</h3>
          <button className="btn ghost" onClick={onClose} style={{ padding: '0 8px' }}>
            <Icon name="x" size={18} />
          </button>
        </div>
        <div className="muted" style={{ fontSize: 13, padding: '14px 22px 0' }}>
          เงินรับล่วงหน้า <b>{advance.voucherNo}</b> · {advance.customerName ?? advance.customerId}
        </div>
        {loadingInvoices ? (
          <div style={{ ...modalBody, color: 'var(--text-muted)' }}>กำลังโหลดใบแจ้งหนี้คงค้าง…</div>
        ) : (
          <ApplyAdvanceForm
            balance={balance}
            openInvoices={openInvoices}
            onSubmit={onSubmit}
            submitting={submitting}
            error={error}
          />
        )}
      </div>
    </div>
  );
}

// =============================================================================
// Refund modal — refund the WHOLE remaining balance back to a company cash/bank
// COA (the same 1110-1119 ASSET picker the receipt/clear screens use) on a chosen
// refund date. Sends { cashAccountCode, refundDate }; the backend posts
// Dr CUSTOMER_ADVANCE / Cr cash for the remaining balance and closes the advance.
// =============================================================================

type CashAccount = Pick<ChartOfAccount, 'code' | 'nameTh'>;

export interface RefundAdvanceModalProps {
  advance: ArAdvance;
  /** The live remaining balance being refunded. */
  balance: number;
  cashAccounts: CashAccount[];
  onSubmit: (data: RefundArAdvance) => void;
  onClose: () => void;
  submitting: boolean;
  error?: { message: string } | null;
}

export function RefundAdvanceModal({
  advance,
  balance,
  cashAccounts,
  onSubmit,
  onClose,
  submitting,
  error,
}: RefundAdvanceModalProps) {
  const today = new Date();
  const [cashAccountCode, setCashAccountCode] = useState<string>('');
  const [refundDate, setRefundDate] = useState(dateToInputValue(today));

  const cashOptions = cashAccounts.map((a) => ({
    value: a.code,
    label: `${a.code} · ${a.nameTh}`,
  }));
  const cashLabel = (code: string) => {
    const hit = cashAccounts.find((a) => a.code === code);
    return hit ? `${hit.code} · ${hit.nameTh}` : code;
  };

  // Defense-in-depth: with a live balance of 0 there is nothing to refund, so
  // block the confirm even before the server re-checks.
  const disabled = !cashAccountCode || !refundDate || submitting || balance <= 0;

  function handleSubmit() {
    onSubmit({ cashAccountCode, refundDate });
  }

  return (
    <div style={modalOverlay} onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} style={{ ...modalBox, width: 520 }}>
        <div style={modalHeader}>
          <h3 style={modalTitle}>คืนเงินรับล่วงหน้า</h3>
          <button className="btn ghost" onClick={onClose} style={{ padding: '0 8px' }}>
            <Icon name="x" size={18} />
          </button>
        </div>
        <div style={modalBody}>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '10px 12px',
              borderRadius: 8,
              background: 'var(--brand-tint)',
              fontSize: 14,
            }}
          >
            <span className="muted">
              คืนเงิน <b>{advance.voucherNo}</b> ส่วนที่เหลือ
            </span>
            <b className="tnum" style={{ fontSize: 16 }}>
              {fmt(balance, 2)} บาท
            </b>
          </div>

          <div>
            <label style={modalLabel}>บัญชีเงินสด / ธนาคาร (คืนเงินจากบัญชี)</label>
            <SearchSelect
              value={cashAccountCode}
              onChange={setCashAccountCode}
              options={cashOptions}
              getLabel={cashLabel}
              placeholder="— เลือกบัญชีธนาคาร —"
            />
          </div>

          <div>
            <label style={modalLabel}>วันที่คืนเงิน</label>
            <input
              type="date"
              className="input"
              aria-label="refund-date"
              value={refundDate}
              onChange={(e) => setRefundDate(e.target.value)}
              style={{ width: '100%', height: 38, fontSize: 14 }}
            />
          </div>

          {error && <div style={modalError}>บันทึกไม่สำเร็จ: {error.message}</div>}
        </div>
        <div style={modalFooter}>
          <button className="btn ghost" onClick={onClose}>
            ยกเลิก
          </button>
          <button className="btn primary" disabled={disabled} onClick={handleSubmit}>
            <Icon name="check" size={14} /> {submitting ? 'กำลังบันทึก…' : 'ยืนยันคืนเงิน'}
          </button>
        </div>
      </div>
    </div>
  );
}
